import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom'; // IMPORTANTE: Para redirigir
import { getLocalYMD } from '../utils/storeLogistics';
import { addDaysYmd, productTracksExpiry, validateLotesEntrada } from '../utils/stockLotes';
import { formatDateOnlyValue } from '../utils/localDateTime';
import { loadRelationalRecipesIfAvailable, loadRecipeBomForSite, findRecipeDefinitionForProduct } from '../utils/productionRecipes';
import { formatCurrency } from '../utils/numberFormat';
import { describirFactorCosto, evaluarRindeProduccion } from '../utils/produccionRinde';

// FIX CRÍTICO #1: helper de idempotencia.
// Cada formulario obtiene un UUID único que se manda al RPC. El RPC debe
// rechazar duplicados (índice único en `produccion.idempotency_key`) para
// evitar doble descuento si el usuario hace doble click o se reintenta tras timeout.
const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback simple para entornos sin crypto.randomUUID (debería ser raro)
  return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

// Helper para namespacear localStorage por organización (evita arrastrar IDs entre orgs)
const lsKey = (orgId, key) => `impulza:${orgId || 'noorg'}:${key}`;
const safeLSGet = (orgId, key, fallback = '') => {
  try { return localStorage.getItem(lsKey(orgId, key)) || fallback; } catch { return fallback; }
};
const safeLSSet = (orgId, key, value) => {
  try { localStorage.setItem(lsKey(orgId, key), value || ''); } catch { /* storage no disponible (modo privado) */ }
};

// Normaliza el nombre de depósito (texto libre hoy, deuda técnica: migrar a tabla con UUID)
const normalizeDepositoNombre = (value) => String(value || '').trim();

// Se produce por LOTE de receta. Costo unitario = costo de insumos / lo que RINDIÓ.
// "Rindió" ya son unidades buenas reales: la merma de la receta solo se usa para
// precargar el rinde esperado, no se vuelve a descontar acá.
const calcCostoUnitario = (costoTotal, rindio) => {
  const divisor = Number(rindio) || 0;
  return divisor > 0 ? Number(costoTotal || 0) / divisor : 0;
};
const round3 = (value) => Math.round((Number(value) || 0) * 1000) / 1000;
const sumCostoInsumos = (items) =>
  (items || []).reduce(
    (acc, item) => acc + Number(item.costoUnitarioInsumo || 0) * Number(item.necesario || 0),
    0,
  );
import { 
  Settings, Package, Truck, Download, 
  CheckCircle2, AlertCircle, ArrowLeft, 
  RefreshCcw, Database, Save, Check, FlaskConical, ClipboardList,
  Search, Filter, Hammer, X, ChevronDown, ChevronRight, DollarSign, Box, ArrowRight, ListPlus
} from 'lucide-react';
import { applyActiveProductsFilter } from '../utils/productStatus';

/**
 * Módulo de Registro de Producción - Estándar Impulza Enterprise
 * Corrección: Buscador Unificado + Redirección a Recetas
 */
function ProduccionRegistrar({ onBack, onNavigate, sitioId }) {
  const { profile, organization } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate(); // Hook para navegación
  
  const [productos, setProductos] = useState([]);
  const [recipes, setRecipes] = useState([]); 
  const [recetasNuevas, setRecetasNuevas] = useState([]); 
  const [sitios, setSitios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  
  // --- ESTADO DEL FORMULARIO ---
  const [selectedProdId, setSelectedProdId] = useState('');
  // Unidades que RINDIÓ la producción: la carga el usuario, nunca se autocompleta.
  const [cantidad, setCantidad] = useState('');
  const [observaciones, setObservaciones] = useState('');
  // Vencimientos y lotes: el producto terminado entra con su lote y vencimiento.
  const [loteProduccion, setLoteProduccion] = useState({ lote_codigo: '', fecha_vencimiento: '' });
  const loteProduccionEditadoRef = useRef(false);
  const [operationalDate, setOperationalDate] = useState(searchParams.get('requestedDate') || getLocalYMD(new Date()));
  const [linkedRequestLineIds, setLinkedRequestLineIds] = useState(() =>
    String(searchParams.get('requestLineIds') || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean),
  );
  const [productionSourceType, setProductionSourceType] = useState(
    searchParams.get('from') === 'pedidos-produccion' ? 'pedido_produccion' : 'manual',
  );

  // --- BUSCADOR UNIFICADO ---
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null); // FIX UX #25: foco automático
  // FIX UX #30: índice del item activo en el dropdown para navegación con flechas
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  // Ubicación (FIX: localStorage namespaced por organización)
  const orgIdForLs = profile?.organization_id || '';
  const [sitioOrigenId, setSitioOrigenId] = useState(safeLSGet(orgIdForLs, 'last_sitio_origen') || sitioId || '');
  const [depositoOrigen, setDepositoOrigen] = useState(safeLSGet(orgIdForLs, 'last_deposito_origen'));
  const [sitioDestinoId, setSitioDestinoId] = useState(safeLSGet(orgIdForLs, 'last_sitio_destino') || sitioId || '');
  const [depositoDestino, setDepositoDestino] = useState(safeLSGet(orgIdForLs, 'last_deposito_destino'));

  const [bom, setBom] = useState([]);
  const [tieneReceta, setTieneReceta] = useState(true);
  const [recipeYield, setRecipeYield] = useState(1); // Unidades que rinde 1 lote de la receta
  // Lotes de receta que se producen: escalan los insumos. `cantidad` es lo que RINDIÓ.
  const [lotes, setLotes] = useState(1);
  // Cantidad pedida por URL (pedidos de producción, en unidades): se pasa a lotes
  // cuando se conoce el rendimiento de la receta.
  const pendingQuantityRef = useRef(0);
  const [bomResetNonce, setBomResetNonce] = useState(0);

  // Stats
  const [stats, setStats] = useState({ costoUnitario: 0, costoTotal: 0, stockPrevio: 0, mermaApplied: false, mermaPct: 0 });
  const [prefillDone, setPrefillDone] = useState(false);

  // FIX UX: flag de organización para aplicar merma al cálculo de costo
  // (controlado desde EmpresaDatos → "Merma Esperada en Recetas").
  const [enableMerma, setEnableMerma] = useState(false);

  // Logico de empresa: permite producir aunque el insumo no alcance. El insumo
  // queda en negativo (es la deuda real de stock) y se acomoda cuando se carga
  // la compra o el ajuste que faltaba. Controlado desde EmpresaDatos.
  const [allowWithoutStock, setAllowWithoutStock] = useState(false);

  // FIX CRÍTICO #1: idempotency_key estable por formulario.
  // Se regenera al hacer reset (después de un guardado exitoso) y al desmontar.
  const idempotencyKeyRef = useRef(generateIdempotencyKey());

  // FIX UX #25: foco automático al primer input al cargar
  useEffect(() => {
    const t = setTimeout(() => searchInputRef.current?.focus(), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // FIX MEJORA: usar la clave estable, no el objeto profile completo (cambia de referencia y dispara fetch en exceso)
    if (profile?.organization_id) fetchInitialData();

    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  useEffect(() => {
    if (prefillDone || productos.length === 0) return;

    const productId = searchParams.get('productId');
    const product = productos.find((item) => item.id === productId);
    const quantityParam = Number(searchParams.get('quantity') || 0);
    const siteIdParam = searchParams.get('siteId') || sitioId || '';
    const requestedDate = searchParams.get('requestedDate');
    const requestLineIds = String(searchParams.get('requestLineIds') || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    const notes = [];

    if (product) {
      setSelectedProdId(product.id);
      setSearchTerm(product.name);
    }

    // El pedido viene en unidades: se usa para calcular los lotes, no como rinde.
    if (quantityParam > 0) pendingQuantityRef.current = quantityParam;
    if (siteIdParam) setSitioDestinoId(siteIdParam);
    if (requestedDate) setOperationalDate(requestedDate);
    if (requestLineIds.length > 0) setLinkedRequestLineIds(requestLineIds);
    if (requestedDate) notes.push(`Pedido para ${requestedDate}`);
    if (searchParams.get('from') === 'pedidos-produccion') {
      setProductionSourceType('pedido_produccion');
      notes.push('Origen: pedidos de produccion');
    }
    if (notes.length > 0) setObservaciones((current) => current || notes.join(' | '));

    setPrefillDone(true);
  }, [prefillDone, productos, searchParams, sitioId]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const orgId = profile.organization_id;
      const [prodsRes, sitsRes, recetasJsonRes] = await Promise.all([
        // Productos a producir e insumos: sin filtro de estado aparecian los
        // dados de baja como si se pudieran producir o consumir.
        applyActiveProductsFilter(
          supabase.from('products').select('*, units_of_measure(abbreviation)').eq('organization_id', orgId)
        ),
        supabase.from('sitios').select('*').eq('organization_id', orgId),
        supabase.from('recetas').select('*').eq('organization_id', orgId),
      ]);
      // FIX UX: enable_recipe_merma sale del AuthContext (sin refetch por página).
      const orgRes = { data: organization, error: null };

      if (prodsRes.error) throw prodsRes.error;
      if (sitsRes.error) throw sitsRes.error;
      if (recetasJsonRes.error) throw recetasJsonRes.error;
      if (orgRes.error && orgRes.error.code !== 'PGRST204') {
        console.warn('[ProduccionRegistrar] No se pudo leer enable_recipe_merma:', orgRes.error);
      }
      setEnableMerma(Boolean(orgRes.data?.enable_recipe_merma));
      setAllowWithoutStock(Boolean(orgRes.data?.allow_production_without_stock));

      const recetasRows = recetasJsonRes.data || [];
      const relationalRecipes =
        recetasRows.length > 0
          ? []
          : await loadRelationalRecipesIfAvailable({
              supabase,
              organizationId: orgId,
              select: '*, yield, recipe_items(*)',
            });

      setProductos(prodsRes.data || []);
      setRecipes(relationalRecipes);
      setSitios(sitsRes.data || []);
      setRecetasNuevas(recetasRows);
    } catch (e) {
      toast.error("Error al cargar datos maestros");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    loteProduccionEditadoRef.current = false;
    setLoteProduccion({ lote_codigo: '', fecha_vencimiento: '' });
    setSelectedProdId('');
    setSearchTerm('');
    setCantidad('');
    setLotes(1);
    setObservaciones('');
    setOperationalDate(getLocalYMD(new Date()));
    setLinkedRequestLineIds([]);
    setProductionSourceType('manual');
    setBom([]);
    setTieneReceta(true);
    setRecipeYield(1);
    setIsSuccess(false);
    setStats({ costoUnitario: 0, costoTotal: 0, stockPrevio: 0, mermaApplied: false, mermaPct: 0 });
    // Nuevo idempotency_key para el siguiente registro (los anteriores ya quedaron protegidos)
    idempotencyKeyRef.current = generateIdempotencyKey();
  };

  // --- LÓGICA DE BOM (por lotes) ---
  // `loadRecipeBomForSite` hace las queries en batch (.in() sobre stock_levels y products).
  // Los insumos se calculan para `lotes` × rendimiento de la receta. Cambiar el rinde
  // (`cantidad`) NO recarga el BOM: solo cambia el costo unitario (derivado abajo).
  useEffect(() => {
    let cancelled = false;
    const loadBom = async () => {
      if (!selectedProdId || !sitioOrigenId) {
        setBom([]);
        setStats(prev => ({ ...prev, costoTotal: 0 }));
        if (!selectedProdId) {
          setTieneReceta(true);
          setRecipeYield(1);
        }
        return;
      }

      const def = findRecipeDefinitionForProduct({ productId: selectedProdId, recetasJson: recetasNuevas, recipes });
      if (!def) {
        setTieneReceta(false);
        setBom([]);
        setRecipeYield(1);
        setStats((prev) => ({ ...prev, costoTotal: 0, mermaApplied: false, mermaPct: 0 }));
        return;
      }
      setTieneReceta(true);
      const yieldDeReceta = Number(def.yield) > 0 ? Number(def.yield) : 1;

      let lotesNum = Number(lotes) || 0;
      // Viene de un pedido de producción en UNIDADES: lo convertimos a lotes una sola vez.
      if (pendingQuantityRef.current > 0) {
        const qty = pendingQuantityRef.current;
        pendingQuantityRef.current = 0;
        lotesNum = round3(qty / yieldDeReceta);
        setLotes(lotesNum);
      }

      try {
        const result = await loadRecipeBomForSite({
          supabase,
          productId: selectedProdId,
          siteId: sitioOrigenId,
          quantity: lotesNum * yieldDeReceta,
          recetasJson: recetasNuevas,
          recipes,
          applyMerma: enableMerma,
        });

        if (cancelled) return;
        if (!result.hasRecipe) {
          setTieneReceta(false);
          setBom([]);
          setRecipeYield(1);
          setStats((prev) => ({ ...prev, costoTotal: 0, mermaApplied: false, mermaPct: 0 }));
          return;
        }

        setRecipeYield(yieldDeReceta);
        setBom(result.bom);

        // Stock previo del producto terminado en destino (1 query adicional)
        const { data: stPT } = await supabase
          .from('stock_levels')
          .select('quantity')
          .eq('product_id', selectedProdId)
          .eq('site_id', sitioDestinoId)
          .maybeSingle();
        if (cancelled) return;

        setStats({
          costoTotal: result.costTotal,
          stockPrevio: Number(stPT?.quantity || 0),
          mermaApplied: Boolean(result.mermaApplied),
          mermaPct: Number(result.mermaPct || 0),
        });
      } catch (err) {
        if (cancelled) return;
        console.error('[ProduccionRegistrar.loadBom] Error:', err);
        toast.error('Error al cargar la composición de la receta');
      }
    };
    loadBom();
    return () => { cancelled = true; };
  }, [selectedProdId, sitioOrigenId, sitioDestinoId, lotes, recipes, recetasNuevas, enableMerma, bomResetNonce]);

  const costoUnitario = calcCostoUnitario(stats.costoTotal, cantidad);
  const factorMermaEsperada = stats.mermaApplied ? 1 - Number(stats.mermaPct || 0) / 100 : 1;
  const rindeEsperado = round3((Number(lotes) || 0) * recipeYield * factorMermaEsperada);
  const desvioRinde = rindeEsperado > 0 && Number(cantidad) > 0
    ? ((Number(cantidad) - rindeEsperado) / rindeEsperado) * 100
    : 0;

  // "Rindió" arranca precargado con lo esperado por la receta (lotes × rinde ×
  // merma). Si el usuario lo edita, se respeta; se vuelve a precargar al cambiar
  // de producto. Antes quedaba vacío y era fácil cargar "1" pensando en lotes.
  const rindioManualRef = useRef(false);
  const productoTerminadoSel = productos.find((p) => p.id === selectedProdId) || null;
  const productoConLote = productTracksExpiry(organization, productoTerminadoSel);

  // Otro producto: vuelve a sugerir (corre antes que el efecto de sugerencia).
  useEffect(() => { loteProduccionEditadoRef.current = false; }, [selectedProdId]);

  // Lote sugerido: L + fecha operativa; vencimiento = fecha + vida útil del producto.
  // Si el usuario lo editó, no se pisa.
  useEffect(() => {
    if (!productoConLote || loteProduccionEditadoRef.current) return;
    const vida = Number(productoTerminadoSel?.vida_util_dias) || 0;
    setLoteProduccion({
      lote_codigo: operationalDate ? `L${operationalDate.replaceAll('-', '')}` : '',
      fecha_vencimiento: vida > 0 && operationalDate ? addDaysYmd(operationalDate, vida) : '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productoConLote, selectedProdId, operationalDate]);

  const handleRindioChange = (value) => {
    rindioManualRef.current = true;
    setCantidad(value);
  };
  useEffect(() => {
    rindioManualRef.current = false;
  }, [selectedProdId]);
  useEffect(() => {
    if (rindioManualRef.current) return;
    if (!selectedProdId || !tieneReceta || !(rindeEsperado > 0)) return;
    setCantidad(String(rindeEsperado));
  }, [rindeEsperado, tieneReceta, selectedProdId]);

  // Descarta ediciones manuales de insumos: vuelve a la receta para los lotes cargados.
  const handleRestablecerReceta = () => {
    setBomResetNonce((n) => n + 1);
  };

  const handleUpdateBomQuantity = (index, newValue) => {
    // FIX CRÍTICO: validar no-negativos y respetar inmutabilidad
    const parsed = parseFloat(newValue);
    const val = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;

    setBom((prev) => {
      const updated = prev.map((item, i) =>
        i === index ? { ...item, necesario: val, falta: item.descuentaStock !== false && Number(item.stockActual || 0) < val } : item,
      );

      const costoTotalInsumos = sumCostoInsumos(updated);
      setStats((s) => ({ ...s, costoTotal: costoTotalInsumos }));
      return updated;
    });
  };

  /**
   * FIX CRÍTICO #2 (mitigación TOCTOU): re-leer stock de los insumos justo antes
   * del submit, con los IDs y el sitio actuales. Reduce ventana de race condition.
   * La validación DEFINITIVA debe estar en el RPC (SELECT FOR UPDATE en stock_levels).
   */
  const revalidarStockAntesDeGuardar = useCallback(async () => {
    // Los insumos de solo costo (descuentaStock=false) no se validan contra stock.
    const bomConStock = bom.filter((item) => item.descuentaStock !== false);
    if (!sitioOrigenId || bomConStock.length === 0) return { ok: true, faltantes: [] };
    const ids = bomConStock.map((item) => item.id).filter(Boolean);
    if (ids.length === 0) return { ok: true, faltantes: [] };

    const { data, error } = await supabase
      .from('stock_levels')
      .select('product_id, quantity')
      .eq('site_id', sitioOrigenId)
      .in('product_id', ids);
    if (error) throw error;

    const stockMap = new Map();
    (data || []).forEach((row) => {
      const key = String(row.product_id);
      stockMap.set(key, Number(stockMap.get(key) || 0) + Number(row.quantity || 0));
    });

    const faltantes = bomConStock
      .map((item) => ({
        ...item,
        stockActualizado: Number(stockMap.get(String(item.id)) || 0),
      }))
      .filter((item) => item.stockActualizado < Number(item.necesario || 0));

    return { ok: faltantes.length === 0, faltantes };
  }, [bom, sitioOrigenId]);

  const handleGuardar = async (e) => {
    e.preventDefault();
    const quantityValue = Number(cantidad);
    if (!selectedProdId || !sitioOrigenId || !sitioDestinoId || !tieneReceta || !operationalDate) {
      return toast.error('Complete todos los campos de origen y destino');
    }
    if (!(Number(lotes) > 0)) {
      return toast.error('Ingresá cuántos lotes produjiste.');
    }
    if (!Number.isFinite(quantityValue) || quantityValue <= 0) {
      return toast.error('Ingresá cuántas unidades rindió la producción.');
    }
    // FIX CRÍTICO: nunca enviar items con cantidad negativa o inválida
    if (bom.some((item) => !Number.isFinite(Number(item.necesario)) || Number(item.necesario) < 0)) {
      return toast.error('Hay insumos con cantidad inválida (negativa o no numérica).');
    }
    if (bom.some((item) => item.falta) && !allowWithoutStock) {
      return toast.error('Stock insuficiente en el sitio de origen');
    }

    // FIX CRÍTICO #8 (mitigación): validar consistencia del depósito seleccionado
    // contra el catálogo del sitio. Hoy depositos son strings; al menos garantizamos
    // que lo guardado existe en la lista del sitio.
    const depOrigenNorm = normalizeDepositoNombre(depositoOrigen);
    const depDestinoNorm = normalizeDepositoNombre(depositoDestino);
    if (depOrigenNorm && !depositosOrigenLista.map(normalizeDepositoNombre).includes(depOrigenNorm)) {
      return toast.error('El depósito de origen no pertenece al sitio seleccionado.');
    }
    if (depDestinoNorm && !depositosDestinoLista.map(normalizeDepositoNombre).includes(depDestinoNorm)) {
      return toast.error('El depósito de destino no pertenece al sitio seleccionado.');
    }

    // FIX UX #24: confirmación previa al consumo masivo. Le mostramos al usuario
    // un resumen de qué insumos va a descontar y de qué sitio, para evitar errores
    // costosos (ej. confundirse de sitio en el dropdown).
    const resumenInsumos = bom
      .slice(0, 5)
      .map((it) => `• ${it.nombre}: ${it.necesario} ${it.um}`)
      .join('\n');
    const sufijoInsumos = bom.length > 5 ? `\n• … y ${bom.length - 5} insumo(s) más` : '';
    const sitioOrigenNombre = sitios.find((s) => s.id === sitioOrigenId)?.nombre || '—';
    const sitioDestinoNombre = sitios.find((s) => s.id === sitioDestinoId)?.nombre || '—';
    // Con el logico de produccion sin stock activo, avisamos explicitamente
    // que insumos van a quedar en negativo: es lo unico que cambia de fondo.
    const enNegativo = allowWithoutStock ? bom.filter((it) => it.falta) : [];
    const avisoNegativo = enNegativo.length > 0
      ? `\nATENCION: estos insumos van a quedar en NEGATIVO:\n${enNegativo
          .slice(0, 5)
          .map((it) => `• ${it.nombre}: ${it.stockActual} - ${it.necesario} ${it.um}`)
          .join('\n')}${enNegativo.length > 5 ? `\n• … y ${enNegativo.length - 5} más` : ''}\n`
      : '';

    // Control de rinde y de salto de costo (utils/produccionRinde.js). El RPC
    // graba el costo unitario en el producto: un rinde mal cargado lo pisa.
    const prodSeleccionado = productos.find((p) => p.id === selectedProdId);
    const costoActualProducto = Number(prodSeleccionado?.cost_price || 0);
    const evaluacion = evaluarRindeProduccion({
      rindio: quantityValue,
      esperado: tieneReceta ? rindeEsperado : 0,
      costoTotal: sumCostoInsumos(bom),
      costoActual: costoActualProducto,
    });
    const lineaCosto = costoActualProducto > 0
      ? `Costo unitario: ${fmtMoney(evaluacion.costoUnitario)} (hoy ${fmtMoney(costoActualProducto)}${evaluacion.factorCosto ? `, ${describirFactorCosto(evaluacion.factorCosto)}` : ''}). Se actualiza en el producto.\n`
      : `Costo unitario: ${fmtMoney(evaluacion.costoUnitario)}. Se guarda en el producto.\n`;

    if (evaluacion.requiereConfirmacion) {
      const motivos = [];
      if (evaluacion.desvioFuerte) {
        motivos.push(
          `• Cargaste que rindió ${quantityValue} un., pero ${lotes} lote(s) de la receta rinden ~${rindeEsperado} un. ` +
          `(${evaluacion.desvioPct > 0 ? '+' : ''}${evaluacion.desvioPct.toFixed(0)}%).`,
        );
      }
      if (evaluacion.saltoCostoFuerte) {
        motivos.push(
          `• El costo del producto pasaría de ${fmtMoney(costoActualProducto)} a ${fmtMoney(evaluacion.costoUnitario)} ` +
          `(${describirFactorCosto(evaluacion.factorCosto)}).`,
        );
      }
      const seguro = window.confirm(
        `ATENCIÓN — revisá antes de seguir:\n\n${motivos.join('\n')}\n\n` +
        `"Rindió" son las UNIDADES que salieron, no los lotes. Si es un error, cancelá y corregilo.\n\n` +
        `¿Es correcto y querés continuar igual?`,
      );
      if (!seguro) return;
    }

    const confirmacion = window.confirm(
      `Vas a registrar la producción de ${quantityValue} unidad(es) de "${productos.find(p=>p.id===selectedProdId)?.name || 'Producto'}".\n` +
      lineaCosto + `\n` +
      `Origen (consumo): ${sitioOrigenNombre}\n` +
      `Destino (PT): ${sitioDestinoNombre}\n\n` +
      `Insumos que se descontarán:\n${resumenInsumos}${sufijoInsumos}\n` +
      avisoNegativo +
      `\n¿Confirmás?`,
    );
    if (!confirmacion) return;

    setSaving(true);
    try {
      // FIX CRÍTICO #2: revalidar stock JUSTO antes del envío para reducir TOCTOU
      const { ok, faltantes } = await revalidarStockAntesDeGuardar();
      if (!ok && allowWithoutStock) {
        // El logico esta activo: no abortamos, pero dejamos el BOM con los
        // stocks frescos para que el usuario vea contra que esta produciendo.
        setBom((prev) => prev.map((item) => {
          const fresco = faltantes.find((f) => f.id === item.id);
          return fresco ? { ...item, stockActual: fresco.stockActualizado, falta: true } : item;
        }));
      } else if (!ok) {
        const detalle = faltantes
          .slice(0, 3)
          .map((f) => `${f.nombre} (disp ${f.stockActualizado} < req ${f.necesario})`)
          .join(', ');
        toast.error(`Stock insuficiente al revalidar: ${detalle}${faltantes.length > 3 ? '…' : ''}`);
        // Refrescamos el BOM con los stocks frescos para que el usuario vea la realidad
        setBom((prev) =>
          prev.map((item) => {
            const fresco = faltantes.find((f) => f.id === item.id);
            return fresco
              ? { ...item, stockActual: fresco.stockActualizado, falta: true }
              : item;
          }),
        );
        return;
      }

      const orgId = profile.organization_id;
      const prodTerminado = productos.find(p => p.id === selectedProdId);
      const conLote = productTracksExpiry(organization, prodTerminado);
      if (conLote) {
        const v = validateLotesEntrada([{ ...loteProduccion, cantidad: quantityValue }], quantityValue);
        if (!v.ok) {
          toast.error(v.error);
          return;
        }
        if (v.vencidos.length > 0
          && !window.confirm(`El vencimiento ${formatDateOnlyValue(loteProduccion.fecha_vencimiento)} ya pasó. ¿Registrar igual?`)) {
          return;
        }
      }

      const costoTotalInsumos = sumCostoInsumos(bom);
      const costoUnitarioFinal = calcCostoUnitario(costoTotalInsumos, quantityValue);
      const payload = {
        organization_id: orgId,
        product_id: selectedProdId,
        product_name: prodTerminado?.name || searchTerm || 'Producto',
        quantity: quantityValue,
        source_site_id: sitioOrigenId,
        source_deposito_id: depOrigenNorm || null,
        target_site_id: sitioDestinoId,
        target_deposito_id: depDestinoNorm || null,
        ingredientes_usados: bom,
        observaciones,
        cost_unitario: costoUnitarioFinal,
        cost_total: costoTotalInsumos,
        operational_date: operationalDate,
        source_type: productionSourceType,
        production_request_line_ids: linkedRequestLineIds,
        // FIX CRÍTICO #1: clave de idempotencia - el RPC debe rechazar duplicados
        // (índice único en produccion.idempotency_key). Ver migración SQL adjunta.
        idempotency_key: idempotencyKeyRef.current,
      };
      if (conLote) {
        payload.lote = {
          lote_codigo: String(loteProduccion.lote_codigo || '').trim() || null,
          fecha_vencimiento: loteProduccion.fecha_vencimiento,
        };
      }
      // Con lote: fn_produccion_con_lote envuelve a register_production_entry
      // (misma transacción) y le pasa el lote al trigger de stock.
      const rpcResponse = await supabase.rpc(conLote ? 'fn_produccion_con_lote' : 'register_production_entry', {
        p_payload: payload,
      });
      if (rpcResponse.error) throw rpcResponse.error;

      // FIX: localStorage namespaced por organización
      safeLSSet(orgId, 'last_sitio_origen', sitioOrigenId);
      safeLSSet(orgId, 'last_deposito_origen', depOrigenNorm);
      safeLSSet(orgId, 'last_sitio_destino', sitioDestinoId);
      safeLSSet(orgId, 'last_deposito_destino', depDestinoNorm);
      setIsSuccess(true);
      toast.success(linkedRequestLineIds.length > 0 ? "PRODUCCION REGISTRADA Y VINCULADA" : "PRODUCCION REGISTRADA");

      setTimeout(() => {
        resetForm();
      }, 2000);
      return;
    } catch (error) {
      // Mapeo amigable de errores comunes que puede devolver el RPC
      const msg = String(error?.message || '');
      if (msg.toLowerCase().includes('idempotency')) {
        toast.error('Esta producción ya fue registrada (operación duplicada bloqueada).');
        // Forzamos nueva clave para próximo intento
        idempotencyKeyRef.current = generateIdempotencyKey();
      } else if (msg.toLowerCase().includes('vencid') || msg.toLowerCase().includes('vencimiento')) {
        toast.error(msg);
      } else if (msg.toLowerCase().includes('stock')) {
        toast.error('Stock insuficiente al confirmar (otro usuario puede haber consumido el insumo).');
      } else {
        toast.error('Error: ' + msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // --- BÚSQUEDA Y SELECCIÓN ---
  const handleSearchChange = (e) => {
    const txt = e.target.value;
    setSearchTerm(txt);
    setIsSearchOpen(true);
    
    // Si el usuario escribe algo nuevo, reseteamos la selección anterior
    // para obligarlo a elegir de la lista y evitar inconsistencias
    if (selectedProdId) {
        setSelectedProdId('');
        setLinkedRequestLineIds([]);
        setProductionSourceType('manual');
        setBom([]);
        setLotes(1);
        setCantidad('');
        setStats({ costoTotal: 0, stockPrevio: 0, mermaApplied: false, mermaPct: 0 });
    }
  };

  const handleSelectProduct = (prod) => {
      if (prod.id !== selectedProdId) {
        setLotes(1);
        setCantidad('');
      }
      setSelectedProdId(prod.id);
      setSearchTerm(prod.name); // El input muestra el nombre seleccionado
      setIsSearchOpen(false);
  };

  // Filtro para el desplegable
  const productosFiltrados = productos.filter(p => {
    const s = searchTerm.toLowerCase();
    // Mostramos si coincide nombre O código de barras
    return p.name.toLowerCase().includes(s) || (p.barcode || '').toLowerCase().includes(s);
  });

  const depositosOrigenLista = sitios.find(s => s.id === sitioOrigenId)?.depositos || [];
  const depositosDestinoLista = sitios.find(s => s.id === sitioDestinoId)?.depositos || [];

  // FIX IMPORTANTE #22: respeta locale/currency de la organización
  const fmtMoney = (n) => formatCurrency(n, { orgId: profile?.organization_id });

  const handleGoBack = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }

    if (searchParams.get('from') === 'pedidos-produccion') {
      const params = new URLSearchParams();
      params.set('date', operationalDate || getLocalYMD(new Date()));
      if (sitioDestinoId) params.set('siteId', sitioDestinoId);
      navigate(`/sistema/pedidos-produccion?${params.toString()}`);
      return;
    }

    navigate(-1);
  };

  if (loading) return (
    <div className="imp-page imp-container">
        <div className="h-full flex flex-col items-center justify-center p-8">
            <div className="w-11 h-11 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-3">
                <RefreshCcw className="text-slate-300 animate-spin" size={18} />
            </div>
            <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">Cargando...</p>
        </div>
    </div>
  );

  return (
    <div className="imp-page imp-container animate-nova-in">
      
      {/* HEADER */}
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Hammer size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">Nueva Producción</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Transformación de stock y cálculo de costos.
            </p>
            {allowWithoutStock && (
              <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-100 text-[8px] font-black uppercase tracking-widest">
                <AlertCircle size={10} /> Producción sin stock habilitada
              </span>
            )}
          </div>
        </div>

        <div className="imp-header-actions flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => navigate('/sistema/produccion-masiva')}
            className="px-5 py-2.5 rounded-2xl bg-blue-50 border border-blue-100 text-[#51A7FD] shadow-sm hover:bg-blue-100 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <ListPlus size={16} /> Produccion masiva
          </button>

          <button 
            type="button"
            onClick={handleGoBack} 
            className="px-5 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-400 hover:text-[#51A7FD] shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
          >
            <ArrowLeft size={16} /> Volver
          </button>
        </div>
      </div>

      {/* KPI SECTION */}
      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Costo Unitario (Est.)</p>
            <DollarSign size={16} className={stats.mermaApplied ? 'text-amber-500' : 'text-slate-300'} />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">
             {costoUnitario > 0 ? fmtMoney(costoUnitario) : '$0.00'}
          </p>
          {/* FIX UX: indicador cuando la merma está siendo aplicada al cálculo */}
          {stats.mermaApplied ? (
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-600">
              Insumos / rindió · merma esperada {stats.mermaPct}%
            </p>
          ) : (
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Insumos / unidades que rindió</p>
          )}
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Rindió</p>
            <Box size={16} className="text-slate-300" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{Number(cantidad) > 0 ? cantidad : '-'}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
            {selectedProdId && tieneReceta
              ? `Unidades en ${Number(lotes) || 0} lote${Number(lotes) === 1 ? '' : 's'}`
              : 'Unidades obtenidas'}
          </p>
        </div>

        <div className="imp-kpi">
            <div className="flex items-center justify-between">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Stock Resultante</p>
                <Database size={16} className="text-slate-300" />
            </div>
            <p className={`mt-1.5 text-2xl font-black tracking-tight ${selectedProdId ? 'text-[#51A7FD]' : 'text-slate-300'}`}>
                {selectedProdId ? (Number(stats.stockPrevio) + Number(cantidad)) : '-'}
            </p>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">En destino</p>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        
        {/* COLUMNA IZQUIERDA: CONFIGURACIÓN */}
        <div className="space-y-4">
          <div className="bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <h2 className="text-[10px] font-black text-[#51A7FD] uppercase tracking-widest mb-4 flex items-center gap-2">
              <Settings size={14} /> Parámetros de Transformación
            </h2>

            <form onSubmit={handleGuardar} className="space-y-4">
              
              {/* --- BUSCADOR UNIFICADO --- */}
              <div className="relative z-20" ref={searchRef}>
                 <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1 mb-1 flex items-center gap-1">
                    <Search size={10} /> Producto a Elaborar
                 </label>
                 
                 <div className="relative">
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="BUSCAR PRODUCTO..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onFocus={() => setIsSearchOpen(true)}
                        // FIX UX #25 + #30: navegación con flechas y Enter en el dropdown
                        onKeyDown={(e) => {
                          if (!isSearchOpen) return;
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setHighlightedIndex((idx) => Math.min(idx + 1, productosFiltrados.length - 1));
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setHighlightedIndex((idx) => Math.max(idx - 1, 0));
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const target = productosFiltrados[highlightedIndex] || productosFiltrados[0];
                            if (target) handleSelectProduct(target);
                            setHighlightedIndex(-1);
                          } else if (e.key === 'Escape') {
                            setIsSearchOpen(false);
                            setHighlightedIndex(-1);
                          }
                        }}
                        role="combobox"
                        aria-expanded={isSearchOpen}
                        aria-autocomplete="list"
                        aria-controls="prod-listbox"
                        aria-activedescendant={
                          highlightedIndex >= 0 && productosFiltrados[highlightedIndex]
                            ? `prod-opt-${productosFiltrados[highlightedIndex].id}`
                            : undefined
                        }
                        className={`w-full pl-11 pr-3 py-3 bg-slate-50 border rounded-2xl text-[11px] font-bold outline-none shadow-inner uppercase text-slate-700
                           ${selectedProdId ? 'border-[#3ff2aa] ring-1 ring-[#3ff2aa]/50' : 'border-slate-100 focus:border-[#51A7FD]'}`}
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                    
                    {/* Botón de limpiar */}
                    {searchTerm && (
                        <button 
                            type="button"
                            onClick={() => {
                              setSearchTerm('');
                              setSelectedProdId('');
                              setLinkedRequestLineIds([]);
                              setProductionSourceType('manual');
                              setIsSearchOpen(false);
                              setBom([]);
                              setTieneReceta(true);
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                        >
                            <X size={14} />
                        </button>
                    )}
                 </div>

                 {/* DROPDOWN DE RESULTADOS - FIX UX #30: accesibilidad */}
                 {isSearchOpen && (
                    <div
                      id="prod-listbox"
                      role="listbox"
                      aria-label="Productos a elaborar"
                      className="absolute top-full left-0 right-0 mt-2 bg-white border border-slate-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto overflow-x-hidden animate-nova-in"
                    >
                        {productosFiltrados.length === 0 ? (
                            <div className="p-4 text-center text-[10px] font-black text-slate-300 uppercase tracking-widest">
                                {searchTerm ? 'Sin coincidencias' : 'Escriba para buscar...'}
                            </div>
                        ) : (
                            productosFiltrados.map((p, idx) => (
                                <button
                                    key={p.id}
                                    id={`prod-opt-${p.id}`}
                                    role="option"
                                    aria-selected={idx === highlightedIndex}
                                    type="button"
                                    onMouseEnter={() => setHighlightedIndex(idx)}
                                    onClick={() => handleSelectProduct(p)}
                                    className={`w-full text-left px-4 py-3 border-b border-slate-50 last:border-0 transition-colors flex items-center justify-between group
                                      ${idx === highlightedIndex ? 'bg-blue-50' : 'hover:bg-slate-50'}`}
                                >
                                    <div>
                                        <p className="text-[10px] font-black text-slate-700 uppercase flex items-center gap-1.5 flex-wrap">
                                            <span>{p.name}</span>
                                            {p.brand && (
                                                <span className="bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-wide">
                                                    {p.brand}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{p.barcode || 'SIN CÓDIGO'}</p>
                                    </div>
                                    <ChevronRight size={14} className="text-slate-300 group-hover:text-[#51A7FD] transition-colors" />
                                </button>
                            ))
                        )}
                    </div>
                 )}
              </div>

              {selectedProdId && tieneReceta ? (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Lotes producidos</label>
                    <input
                      type="number" min="0" step="any" required value={lotes}
                      onChange={(e) => setLotes(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-black text-[#51A7FD] outline-none shadow-inner focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10"
                    />
                    <p className="text-[9px] font-bold text-slate-400 ml-1">
                      {recipeYield > 1
                        ? `Veces que hiciste la receta (1 lote ≈ ${recipeYield} un.). Calcula los insumos.`
                        : 'Veces que hiciste la receta. Calcula los insumos.'}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Rindió (unidades)</label>
                    <input
                      type="number" min="0" step="any" required value={cantidad}
                      onChange={(e) => handleRindioChange(e.target.value)}
                      onFocus={(e) => e.target.select()}
                      placeholder="Unidades obtenidas"
                      className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-black text-[#51A7FD] outline-none shadow-inner focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10"
                    />
                    {recipeYield > 1 && rindeEsperado > 0 && Number(cantidad) > 0 && Math.abs(desvioRinde) >= 0.05 ? (
                      <p className={`text-[9px] font-bold ml-1 ${desvioRinde < 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        Esperado {rindeEsperado} un. ({desvioRinde > 0 ? '+' : ''}{desvioRinde.toFixed(1)}%). Ajusta el costo unitario.
                      </p>
                    ) : (
                      <p className="text-[9px] font-bold text-slate-400 ml-1">
                        {Number(cantidad) > 0
                          ? (recipeYield > 1 ? 'Coincide con lo esperado por la receta.' : 'Divide el costo de los insumos.')
                          : `Cargá cuántas unidades salieron${recipeYield > 1 ? ` (receta: ${rindeEsperado} un.)` : ''}.`}
                      </p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Cantidad obtenida</label>
                  <input
                    type="number" min="0" step="any" required value={cantidad}
                    onChange={(e) => handleRindioChange(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-black text-[#51A7FD] outline-none shadow-inner focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10"
                  />
                </div>
              )}

              <div className="space-y-1">
                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest ml-1">Fecha Operativa</label>
                <input
                  type="date"
                  required
                  value={operationalDate}
                  onChange={e => setOperationalDate(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-100 px-3 py-2.5 rounded-xl text-[11px] font-black text-slate-700 outline-none shadow-inner focus:border-[#51A7FD] focus:ring-2 focus:ring-[#51A7FD]/10"
                />
                <p className="text-[9px] font-bold text-slate-400">
                  Define en que dia operativo debe impactar esta produccion en logistica y pedidos de produccion.
                </p>
              </div>

              {/* RUTEO DE INVENTARIO */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100 space-y-2">
                  <div className="flex items-center gap-2 text-[8px] font-black text-slate-400 uppercase tracking-widest">
                    <Truck size={12} /> Origen (Materia Prima)
                  </div>
                  <select 
                    value={sitioOrigenId} onChange={e => setSitioOrigenId(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-[10px] font-bold outline-none cursor-pointer"
                  >
                    <option value="">Sitio...</option>
                    {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                  <select 
                    value={depositoOrigen} onChange={e => setDepositoOrigen(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-[10px] font-bold outline-none cursor-pointer"
                  >
                    <option value="">Depósito...</option>
                    {depositosOrigenLista.map((d, i) => <option key={i} value={d}>{d}</option>)}
                  </select>
                </div>

                <div className="p-3 bg-blue-50/30 rounded-2xl border border-blue-100 space-y-2">
                  <div className="flex items-center gap-2 text-[8px] font-black text-[#51A7FD] uppercase tracking-widest">
                    <Download size={12} /> Destino (Prod. Terminado)
                  </div>
                  <select 
                    value={sitioDestinoId} onChange={e => setSitioDestinoId(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-[10px] font-bold outline-none cursor-pointer"
                  >
                    <option value="">Sitio...</option>
                    {sitios.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                  </select>
                  <select 
                    value={depositoDestino} onChange={e => setDepositoDestino(e.target.value)}
                    className="w-full bg-white border border-slate-200 rounded-xl py-2 px-3 text-[10px] font-bold outline-none cursor-pointer"
                  >
                    <option value="">Depósito...</option>
                    {depositosDestinoLista.map((d, i) => <option key={i} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>

              {productoConLote && (
                <div className="p-3 rounded-2xl border border-violet-200 bg-violet-50/60 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="space-y-1">
                    <span className="text-[8px] font-black text-violet-700 uppercase tracking-widest ml-1">Lote producido</span>
                    <input
                      type="text"
                      value={loteProduccion.lote_codigo}
                      onChange={e => { loteProduccionEditadoRef.current = true; setLoteProduccion(l => ({ ...l, lote_codigo: e.target.value.toUpperCase() })); }}
                      className="w-full bg-white border border-slate-200 px-3 py-2 rounded-xl text-[11px] font-black uppercase outline-none"
                    />
                  </label>
                  <label className="space-y-1">
                    <span className="text-[8px] font-black text-violet-700 uppercase tracking-widest ml-1">Vencimiento *</span>
                    <input
                      type="date"
                      value={loteProduccion.fecha_vencimiento}
                      onChange={e => { loteProduccionEditadoRef.current = true; setLoteProduccion(l => ({ ...l, fecha_vencimiento: e.target.value })); }}
                      className={`w-full bg-white border px-3 py-2 rounded-xl text-[11px] font-black outline-none ${loteProduccion.fecha_vencimiento ? 'border-slate-200' : 'border-amber-300'}`}
                    />
                  </label>
                </div>
              )}

              <textarea 
                value={observaciones} onChange={e => setObservaciones(e.target.value)}
                placeholder="NOTAS TÉCNICAS O NRO DE LOTE..."
                className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-3 px-4 text-[10px] font-bold uppercase outline-none focus:border-[#51A7FD] transition-all h-20 shadow-inner resize-none"
              />

              {linkedRequestLineIds.length > 0 && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-bold text-emerald-800">
                  Esta produccion quedara vinculada a {linkedRequestLineIds.length} linea(s) del pedido productivo.
                </div>
              )}

              <div className="pt-2">
                <button 
                  disabled={saving || isSuccess || !tieneReceta || !selectedProdId} 
                  type="submit" 
                  className={`w-full py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest transition-all flex items-center justify-center gap-2 shadow-lg
                    ${isSuccess 
                      ? 'bg-[#3ff2aa] text-[#0B2A36] scale-105 shadow-emerald-100' 
                      : 'bg-[#3ff2aa] text-[#0B2A36] hover:scale-[1.02] active:scale-95 shadow-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed'}`}
                >
                  {isSuccess ? <><Check size={18} strokeWidth={3} /> Procesado</> : saving ? <><RefreshCcw size={18} className="animate-spin" /> Registrando...</> : <><Save size={18} /> Confirmar Producción</>}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* COLUMNA DERECHA: BOM O ALERTAS */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-full min-h-[400px]">
            <div className="bg-[#0B2A36] px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-2">
                <FlaskConical size={16} className="text-[#3FF2AA]" />
                <span className="text-[10px] font-black text-white uppercase tracking-widest">
                    Insumos Requeridos
                </span>
                <div className="ml-auto flex items-center gap-3">
                  {selectedProdId && tieneReceta && bom.length > 0 && (
                    <button
                      type="button"
                      onClick={handleRestablecerReceta}
                      title="Descarta las ediciones manuales y vuelve a la receta para los lotes cargados."
                      className="flex items-center gap-1 text-[8px] font-black uppercase tracking-widest text-white/60 hover:text-[#3FF2AA] transition-colors"
                    >
                      <RefreshCcw size={11} /> Restablecer a receta base
                    </button>
                  )}
                </div>
            </div>
            
            <div className="p-4 flex-1 flex flex-col">
                {/* ESTADO 1: SIN SELECCIÓN */}
                {!selectedProdId && (
                    <div className="flex-1 flex flex-col items-center justify-center text-slate-300 opacity-50">
                        <ClipboardList size={40} strokeWidth={1} className="mb-3" />
                        <p className="text-[9px] font-black uppercase tracking-widest">Seleccione un producto</p>
                    </div>
                )}

                {/* ESTADO 2: SELECCIONADO PERO SIN RECETA -> BOTÓN DE ACCIÓN */}
                {selectedProdId && !tieneReceta && (
                    <div className="flex-1 flex flex-col items-center justify-center animate-nova-in">
                        <div className="w-16 h-16 rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center mb-4 shadow-sm animate-pulse">
                            <AlertCircle size={32} className="text-rose-500" />
                        </div>
                        <h3 className="text-sm font-black text-slate-800 uppercase tracking-tight mb-2">Sin Receta Definida</h3>
                        <p className="text-[10px] text-slate-500 text-center max-w-[250px] mb-6">
                            El producto seleccionado no tiene una estructura de costos (BOM) cargada en el sistema.
                        </p>
                        <button 
                            type="button"
                            onClick={() => navigate('/sistema/recetas')}
                            className="px-6 py-3 rounded-2xl bg-rose-500 text-white shadow-lg shadow-rose-200 hover:shadow-xl hover:bg-rose-600 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
                        >
                            <FlaskConical size={16} /> Ir a Cargar Receta <ArrowRight size={14}/>
                        </button>
                    </div>
                )}

                {/* ESTADO 3: CON RECETA -> TABLA */}
                {selectedProdId && tieneReceta && (
                    <div className="overflow-x-auto imp-table-wrap animate-nova-in">
                        <table className="w-full text-left">
                            <thead>
                            <tr className="bg-slate-50/60 border-b border-slate-100 text-[9px] font-black text-slate-400 uppercase tracking-widest">
                                <th className="pb-3 pl-2 pt-2">Ingrediente</th>
                                <th className="pb-3 pt-2 text-center w-28">A Descontar</th>
                                <th className="pb-3 pr-2 pt-2 text-right">Estado Stock</th>
                            </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                            {bom.map((item, i) => (
                                <tr key={i} className="hover:bg-slate-50 transition-colors">
                                <td className="py-3 pl-2">
                                    <p className="text-[10px] font-black text-slate-700 uppercase leading-tight">{item.nombre}</p>
                                    <p className="text-[8px] font-bold text-slate-400 uppercase mt-0.5">Costo Unit: {fmtMoney(item.costoUnitarioInsumo)}</p>
                                </td>
                                <td className="py-3 text-center">
                                    <div className="flex items-center justify-center gap-1">
                                    <input 
                                        type="number" step="any"
                                        value={item.necesario}
                                        onChange={(e) => handleUpdateBomQuantity(i, e.target.value)}
                                        className="w-16 bg-slate-50 border border-slate-100 rounded-xl py-1 px-1 text-[10px] font-black text-[#51A7FD] outline-none text-center shadow-inner focus:border-[#51A7FD]"
                                    />
                                    <span className="text-[8px] text-slate-400 uppercase font-bold">{item.um}</span>
                                    </div>
                                </td>
                                <td className="py-3 pr-2 text-right">
                                    {item.descuentaStock === false ? (
                                    <div className="flex flex-col items-end">
                                        <span
                                          className="text-[8px] font-black uppercase px-2 py-1 rounded-lg border text-slate-500 bg-slate-50 border-slate-200"
                                          title="Suma al costo pero no descuenta stock (se configura en la ficha del producto)"
                                        >
                                          Solo costo
                                        </span>
                                        <span className="text-[8px] text-slate-400 font-bold mt-1">No descuenta stock</span>
                                    </div>
                                    ) : item.falta ? (
                                    <div className="flex flex-col items-end">
                                        <span className={`text-[8px] font-black uppercase px-2 py-1 rounded-lg border ${
                                          allowWithoutStock
                                            ? 'text-amber-600 bg-amber-50 border-amber-100'
                                            : 'text-rose-500 bg-rose-50 border-rose-100'
                                        }`}>
                                          {allowWithoutStock ? 'Queda en negativo' : 'Crítico'}
                                        </span>
                                        <span className="text-[8px] text-slate-400 font-bold mt-1">Disp: {item.stockActual}</span>
                                    </div>
                                    ) : (
                                    <div className="flex flex-col items-end">
                                        <span className="text-[8px] text-emerald-500 font-black uppercase bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">OK</span>
                                        <span className="text-[8px] text-slate-400 font-bold mt-1">Disp: {item.stockActual}</span>
                                    </div>
                                    )}
                                </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
            
            {/* FOOTER TOTAL (Solo si hay items) */}
            {bom.length > 0 && (
                <div className="bg-slate-50 p-3 border-t border-slate-100 flex justify-between items-center">
                    <span className="text-[9px] font-bold uppercase text-slate-400 tracking-widest">
                      Costo total ({Number(lotes) || 0} lote{Number(lotes) === 1 ? '' : 's'})
                    </span>
                    <span className="text-[11px] font-black text-slate-700">
                      {fmtMoney(stats.costoTotal)}
                      <span className="ml-2 text-[9px] font-bold text-slate-400">({fmtMoney(costoUnitario)} / un.)</span>
                    </span>
                </div>
            )}
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .imp-table-wrap { width: 100%; overflow-x: auto; }
      `}</style>
    </div>
  );
}

export default ProduccionRegistrar;
